import axios from 'axios';
import dynamic from 'next/dynamic';
import { useState, useEffect, useRef, Fragment } from 'react';
import { useLocationState } from '../../context/location';
import { sleep, toMoney } from '../../helpers';
import { submitClientRequest } from '../../helpers/clientRequest';
import Button from '../Button';
import Input from '../Input';
import { useIsMount } from '../../hooks/useIsMount';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from '../../styles/modules/building/BuildingObjectsList.module.scss';
import FormAgreement from '../FormAgreement';

const Modal = dynamic(() => import('../Modal'));

const getShortTitle = (t) => {
  if (t === 'studio') return 'СТ';

  return `${t}К`;
};
const getMediumTitle = (t) => {
  if (t === 'studio') return 'Студия';

  return `${t}-комнатная`;
};
const getLongTitle = (t) => {
  if (t === 'studio') return 'Квартира-студия';

  switch (t) {
    case 1:
      return 'Однокомнатная квартира';

    case 2:
      return 'Двухкомнатная квартира';

    case 3:
      return 'Трехкомнатная квартира';

    case 4:
      return 'Четырехкомнатная квартира';

    default:
      return '';
  }
};

const BuildingObjectsList = ({ flats, total, filters }) => {
  const isMount = useIsMount();
  const locationState = useLocationState();
  const { width } = useWindowDimensions();
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isSubmited, setIsSubmited] = useState(false);
  const [allFlats, setAllFlats] = useState(flats);
  const [page, setPage] = useState(1);
  const [clickedFlat, setClickedFlats] = useState(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [agreeWithPolicy, setAgreeWithPolicy] = useState(false);
  const clientNameId = useRef();
  const clientPhoneId = useRef();

  const handleLoadMoreClick = async () => {
    setIsLoading(true);

    await sleep(300);
    setPage(page + 1);
  };
  const handleFormSubmit = () => {
    clientNameId.current.setError(clientName.length <= 3);
    clientPhoneId.current.setError(clientPhone.length !== 11);

    if (clientName.length > 3 && clientPhone.length === 11 && agreeWithPolicy) {
      submitClientRequest({
        clientName,
        clientPhone: clientPhone,
        pathName: window.location.pathname,
        whatComponent: 'BuildingObjectsList',
        adsNumber: clickedFlat.adsNumber,
        location: locationState.name,
        platform: 'web'
      }).then((isSubmitted) => {
        if (isSubmitted) setIsSubmited(true);
      });
    }
  };

  useEffect(() => {
    if (isMount) return;

    const fetchData = async () => {
      try {
        const response = await axios.post(process.env.NEXT_PUBLIC_API + '/buildings', {
          ...filters,
          page
        });

        if (!response.data || response.data.status !== 'ok') throw new Error(response.data.text);

        setAllFlats([...allFlats, ...response.data.data.data]);
        setIsLoading(false);
      } catch (error) {}
    };
    fetchData();
  }, [page]);

  return (
    <Fragment>
      <Modal
        button={!isSubmited && 'Получить консультацию'}
        action={handleFormSubmit}
        visibility={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        buttonDisabled={!agreeWithPolicy}
      >
        {isSubmited ? (
          <Fragment>
            <div style={{ marginBottom: 18 }}>Спасибо за заявку. Мы скоро с Вами свяжемся!</div>
            <img
              src="/images/thumbUp.png"
              alt=""
              style={{ padding: 50, width: '100%', boxSizing: 'border-box' }}
            />
          </Fragment>
        ) : (
          <Fragment>
            {clickedFlat && (
              <div className={styles.smallPopPreview}>
                <div className={styles.smallPopPreviewImage}>
                  <img src={clickedFlat.plan} alt="Планировка" />
                </div>
                <div className={styles.smallPopPreviewTitle}>
                  {getLongTitle(clickedFlat.studio ? 'studio' : clickedFlat.rooms)}
                </div>
                <div className={styles.smallPopPreviewLabels}>
                  <span>{getMediumTitle(clickedFlat.studio ? 'studio' : clickedFlat.rooms)}</span>
                  <span>{Number(clickedFlat.area.value).toFixed(2)} м²</span>
                  <span>{toMoney(clickedFlat.price.value)} ₽</span>
                </div>
                <div className={styles.smallPopPreviewFree}>Услуги компании бесплатно</div>
              </div>
            )}
            <div style={{ margin: '18px -12px', borderBottom: '1px dashed rgba(0, 0, 0, 0.05)' }} />
            <Input
              title="Имя"
              inputs={[
                {
                  placeholder: 'Василий Петрович',
                  value: clientName,
                  type: 'text'
                }
              ]}
              isWrappedStyle
              ref={clientNameId}
              onChange={(v) => setClientName(v[0])}
            />
            <Input
              title="Телефон"
              inputs={[
                {
                  placeholder: locationState.phone,
                  value: clientPhone,
                  mask: 'phone',
                  type: 'tel'
                }
              ]}
              isWrappedStyle
              ref={clientPhoneId}
              onChange={(v) => setClientPhone(v[0])}
            />
            <FormAgreement onChange={setAgreeWithPolicy} />
          </Fragment>
        )}
      </Modal>
      <div className={styles.list}>
        {allFlats.map((value, index) => {
          const planID = Object.keys(value.images).find((key) => value.images[key].tag === 'plan');

          return (
            <div
              key={index}
              role="button"
              tabIndex={0}
              className={styles.listItem}
              onClick={() => {
                setClickedFlats({
                  plan: value.images[planID].value,
                  studio: value.studio,
                  rooms: value.rooms,
                  area: value.area,
                  adsNumber: value['ads-number'],
                  price: value.price
                });
                setIsModalVisible(true);
              }}
            >
              <div className={styles.listItemImage}>
                <img src={value.images[planID].value} alt="Планировка" width={60} height={60} />
              </div>
              <div className={styles.listItemText}>
                {getShortTitle(value.studio ? 'studio' : value.rooms)}
              </div>
              <div className={styles.listItemText}>{Number(value.area.value).toFixed(2)} м²</div>
              <div className={styles.listItemText}>
                {value.floor} {width > 768 ? 'этаж из' : '/'} {value['floors-total']}
              </div>
              <div className={styles.listItemText}>
                <div
                  className={[
                    styles.listItemTextPrice,
                    filters.priceUnit === 'all' && styles.accent
                  ].join(' ')}
                >
                  {toMoney(value.price.value)} ₽
                </div>
                <div
                  className={[
                    styles.listItemTextPrice,
                    filters.priceUnit === 'm2' && styles.accent
                  ].join(' ')}
                >
                  {toMoney(value.priceM2)} ₽/м²
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {page * 10 < total && (
        <div className={styles.more}>
          <Button onClick={handleLoadMoreClick} fullWidth={width < 768} loading={isLoading}>
            Показать еще 10 квартир из {total}
          </Button>
        </div>
      )}
    </Fragment>
  );
};

export default BuildingObjectsList;
